import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/uk';
import isoWeek from 'dayjs/plugin/isoWeek';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
dayjs.locale('uk');

/** Часовий пояс організації (правило 11 AGENTS.md). */
export const ORG_TZ = 'Europe/Kyiv';

export { dayjs };
export type { Dayjs };

/** Поточний момент у часовому поясі організації. */
export function nowKyiv(): Dayjs {
  return dayjs().tz(ORG_TZ);
}

/** Сьогоднішня дата за Києвом у форматі РРРР-ММ-ДД. */
export function todayISO(): string {
  return nowKyiv().format('YYYY-MM-DD');
}

/** Дата без часу як об'єкт dayjs (полудень UTC, щоб уникнути зсуву дня). */
export function parseDate(iso: string): Dayjs {
  return dayjs.utc(`${iso.slice(0, 10)}T12:00:00Z`);
}

export function toISODate(d: Dayjs): string {
  return d.format('YYYY-MM-DD');
}

export function addDaysISO(iso: string, days: number): string {
  return toISODate(parseDate(iso).add(days, 'day'));
}

/** Різниця в днях між датами (b − a). */
export function diffDays(a: string, b: string): number {
  return parseDate(b).diff(parseDate(a), 'day');
}

export function isWeekend(iso: string): boolean {
  const d = parseDate(iso).isoWeekday();
  return d === 6 || d === 7;
}

/** Наступний робочий день (пн–пт) після вказаної дати. Святкові дні задає календар сервера. */
export function nextWorkingDayISO(fromISO: string = todayISO()): string {
  let d = parseDate(fromISO).add(1, 'day');
  while (d.isoWeekday() > 5) d = d.add(1, 'day');
  return toISODate(d);
}

/** Понеділок наступного тижня. */
export function nextMondayISO(fromISO: string = todayISO()): string {
  const d = parseDate(fromISO);
  return toISODate(d.add(8 - d.isoWeekday(), 'day'));
}

const fmtDayMonth = new Intl.DateTimeFormat('uk-UA', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const fmtDayMonthYear = new Intl.DateTimeFormat('uk-UA', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
const fmtLong = new Intl.DateTimeFormat('uk-UA', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
const fmtFull = new Intl.DateTimeFormat('uk-UA', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
const fmtMonthYear = new Intl.DateTimeFormat('uk-UA', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const fmtNumeric = new Intl.DateTimeFormat('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
const fmtTime = new Intl.DateTimeFormat('uk-UA', { hour: '2-digit', minute: '2-digit', timeZone: ORG_TZ });
const fmtDateTime = new Intl.DateTimeFormat('uk-UA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: ORG_TZ });
const fmtDateTimeYear = new Intl.DateTimeFormat('uk-UA', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: ORG_TZ,
});
const fmtWeekdayShort = new Intl.DateTimeFormat('uk-UA', { weekday: 'short', timeZone: 'UTC' });

function asUtcDate(iso: string): Date {
  return new Date(`${iso.slice(0, 10)}T12:00:00Z`);
}

/** «24 вер.» або «24 вер. 2027 р.», якщо рік не поточний. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = asUtcDate(iso);
  const sameYear = iso.slice(0, 4) === todayISO().slice(0, 4);
  return (sameYear ? fmtDayMonth : fmtDayMonthYear).format(d);
}

/** «24.09.2026» */
export function formatDateNumeric(iso: string | null | undefined): string {
  if (!iso) return '';
  return fmtNumeric.format(asUtcDate(iso));
}

/** «24 вересня 2026 р.» */
export function formatDateFull(iso: string | null | undefined): string {
  if (!iso) return '';
  return fmtFull.format(asUtcDate(iso));
}

/** «четвер, 24 вересня» */
export function formatDateLong(iso: string): string {
  const s = fmtLong.format(asUtcDate(iso));
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** «вересень 2026 р.» → «Вересень 2026» */
export function formatMonthYear(iso: string): string {
  const s = fmtMonthYear.format(asUtcDate(iso)).replace(/\s*р\.$/, '');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function formatWeekdayShort(iso: string): string {
  return fmtWeekdayShort.format(asUtcDate(iso));
}

/** Строк відносно сьогодні: «Сьогодні», «Завтра», «Вчора», «пн», інакше дата. */
export function formatDue(iso: string | null | undefined): string {
  if (!iso) return 'Без строку';
  const today = todayISO();
  const diff = diffDays(today, iso);
  if (diff === 0) return 'Сьогодні';
  if (diff === 1) return 'Завтра';
  if (diff === -1) return 'Вчора';
  if (diff > 1 && diff < 7) {
    const wd = formatWeekdayShort(iso);
    return wd.charAt(0).toUpperCase() + wd.slice(1);
  }
  return formatDate(iso);
}

/** Момент часу (ISO 8601) за Києвом: «24 вер., 14:05». */
export function formatDateTime(isoDateTime: string | null | undefined): string {
  if (!isoDateTime) return '';
  const d = new Date(isoDateTime);
  const sameYear = dayjs(d).tz(ORG_TZ).year() === nowKyiv().year();
  return (sameYear ? fmtDateTime : fmtDateTimeYear).format(d);
}

export function formatTime(isoDateTime: string): string {
  return fmtTime.format(new Date(isoDateTime));
}

const rtf = new Intl.RelativeTimeFormat('uk-UA', { numeric: 'auto' });

/** «щойно», «5 хв тому», «2 год тому», «вчора», інакше дата й час. */
export function formatRelative(isoDateTime: string | null | undefined): string {
  if (!isoDateTime) return '';
  const diffSec = (Date.now() - new Date(isoDateTime).getTime()) / 1000;
  if (diffSec < 45) return 'щойно';
  if (diffSec < 3600) return rtf.format(-Math.round(diffSec / 60), 'minute');
  if (diffSec < 6 * 3600) return rtf.format(-Math.round(diffSec / 3600), 'hour');
  const then = dayjs(isoDateTime).tz(ORG_TZ).format('YYYY-MM-DD');
  const days = diffDays(then, todayISO());
  if (days === 0) return `сьогодні, ${formatTime(isoDateTime)}`;
  if (days === 1) return `вчора, ${formatTime(isoDateTime)}`;
  return formatDateTime(isoDateTime);
}

/** Робочі години між моментом і зараз за типовим графіком пн–пт 09:00–18:00 (орієнтовно). */
export function workingHoursSince(isoDateTime: string, schedule = { start: 9, end: 18 }): number {
  let cur = dayjs(isoDateTime).tz(ORG_TZ);
  const end = nowKyiv();
  if (!cur.isValid() || cur.isAfter(end)) return 0;
  let hours = 0;
  let guard = 0;
  while (cur.isBefore(end) && guard < 400) {
    guard++;
    const wd = cur.isoWeekday();
    const dayStart = cur.hour(schedule.start).minute(0).second(0);
    const dayEnd = cur.hour(schedule.end).minute(0).second(0);
    if (wd <= 5) {
      const from = cur.isAfter(dayStart) ? cur : dayStart;
      const to = end.isBefore(dayEnd) ? end : dayEnd;
      if (to.isAfter(from)) hours += to.diff(from, 'minute') / 60;
    }
    cur = cur.add(1, 'day').hour(0).minute(0).second(0);
  }
  return hours;
}

/** Поточна година за Києвом (для правила «після 15:00»). */
export function kyivHour(): number {
  return nowKyiv().hour();
}

/** Сітка місяця для календаря: тижні з понеділка, 6 × 7 днів. */
export function monthGrid(anyDayISO: string): string[][] {
  const first = parseDate(anyDayISO).startOf('month');
  const start = first.subtract(first.isoWeekday() - 1, 'day');
  const weeks: string[][] = [];
  for (let w = 0; w < 6; w++) {
    const row: string[] = [];
    for (let d = 0; d < 7; d++) row.push(toISODate(start.add(w * 7 + d, 'day')));
    weeks.push(row);
  }
  return weeks;
}

export const WEEKDAYS_SHORT = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'нд'];
